import React from 'react';
import { Button } from '@oysterjs/ui/button';
import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Textarea } from '@oysterjs/ui/textarea';
import { Slideout } from '@oysterjs/ui/slideout';
import {
  VoiceAgentSipTransferMode,
  VoiceAgentTransferDirectoryEntryType,
  VoiceAgentTransferCallType,
  VoiceAgentTransferType
} from '~/api/graphql';
import { ProLabel } from '~/components/pro-label';
import {
  isTransferDirectoryExtensionEditable,
  type VoiceAgentSettingsTransferDirectoryEntryInput,
  type VoiceAgentTransferLineOfBusinessInput
} from './voice_agent_form_input';
import { TransferDirectoryEntryFields } from './voice_transfer_directory_entry_fields';
import { PRO_ENTRY_LIMIT, TransferDirectoryTable } from './voice_transfer_directory_table';
import { toE164 } from '~/lib/phone';

const PRO_UPGRADE_HINT = `Upgrade to Pro to add more than ${PRO_ENTRY_LIMIT} transfer directory entries`;

export function TransferDirectorySection({
  sectionLabel,
  entryType,
  description,
  directory,
  onUpdateDirectory,
  hasAdvancedCustomization,
  useStructured,
  voice35Enabled,
  transferType,
  sipTransferMode,
  getFieldId,
  getValidationError,
  hasRowError,
  openEntryIndex,
  setOpenEntryIndex,
  lineOfBusinessCatalog
}: {
  sectionLabel: string;
  entryType: VoiceAgentTransferDirectoryEntryType;
  description: React.ReactNode;
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[];
  onUpdateDirectory: (newDirectory: VoiceAgentSettingsTransferDirectoryEntryInput[]) => void;
  hasAdvancedCustomization: boolean | undefined;
  useStructured: boolean;
  /** When false, render the pre–new-cold-transfer directory UI unchanged (main branch). */
  voice35Enabled: boolean;
  transferType: VoiceAgentTransferType;
  sipTransferMode?: VoiceAgentSipTransferMode | null;
  getFieldId: (path: (string | number)[]) => string;
  getValidationError: (path: string[]) => string | undefined;
  hasRowError: (fullIndex: number) => boolean;
  /** Shared by the internal and external sections so only one slideout is open. */
  openEntryIndex: number | null;
  setOpenEntryIndex: React.Dispatch<React.SetStateAction<number | null>>;
  lineOfBusinessCatalog: VoiceAgentTransferLineOfBusinessInput[];
}) {
  const useLegacyExact = !voice35Enabled;
  const extensionEditable = isTransferDirectoryExtensionEditable(
    transferType,
    sipTransferMode || VoiceAgentSipTransferMode.Invite
  );
  const ownEntries = useLegacyExact
    ? directory.map((entry, fullIndex) => ({ entry, fullIndex }))
    : directory
        .map((entry, fullIndex) => ({ entry, fullIndex }))
        .filter(({ entry }) =>
          entryType === VoiceAgentTransferDirectoryEntryType.Internal
            ? !entry.entryType || entry.entryType === VoiceAgentTransferDirectoryEntryType.Internal
            : entry.entryType === entryType
        );

  const isProOnlyEntry = (fullIndex: number) =>
    fullIndex >= PRO_ENTRY_LIMIT && !hasAdvancedCustomization;
  const addDisabled = !hasAdvancedCustomization && directory.length >= PRO_ENTRY_LIMIT;

  const updateEntry = (
    fullIndex: number,
    patch: Partial<VoiceAgentSettingsTransferDirectoryEntryInput>
  ) => {
    const newDirectory = [...directory];
    newDirectory[fullIndex] = { ...newDirectory[fullIndex], ...patch };
    onUpdateDirectory(newDirectory);
  };

  const removeEntry = (fullIndex: number) => {
    onUpdateDirectory(directory.filter((_, i) => i !== fullIndex));
  };

  const removeEntries = (fullIndexes: number[]) => {
    const removing = new Set(fullIndexes);
    onUpdateDirectory(directory.filter((_, i) => !removing.has(i)));
    setOpenEntryIndex(null);
  };

  const addEntry = () => {
    const newFullIndex = directory.length;
    const newDirectory = [
      ...directory,
      useStructured
        ? {
            label: '',
            number: '',
            transferCriteria: '',
            entryType,
            callTypes: [] as VoiceAgentTransferCallType[],
            lineOfBusinessIds: [] as string[],
            aliases: [] as string[],
            extension: undefined,
            servicingCode: undefined
          }
        : {
            label: '',
            number: '',
            transferCriteria: '',
            lineOfBusinessIds: []
          }
    ];
    onUpdateDirectory(newDirectory);
    if (useStructured) {
      setOpenEntryIndex(newFullIndex);
    }
  };

  const fieldError = getValidationError(['configuration', 'transfer', 'directory']);

  // Only the owning section mounts the slideout.
  const openEntry =
    openEntryIndex !== null && ownEntries.some(({ fullIndex }) => fullIndex === openEntryIndex)
      ? directory[openEntryIndex]
      : undefined;

  return (
    <Field data-field-id={getFieldId(['configuration', 'transfer', 'directory'])}>
      <Label>{sectionLabel}</Label>
      {description}
      {fieldError && <ErrorMessage>{fieldError}</ErrorMessage>}

      <div className="space-y-4" data-slot="control">
        {useStructured ? (
          <>
            <TransferDirectoryTable
              rows={ownEntries}
              entryTypeNoun={
                entryType === VoiceAgentTransferDirectoryEntryType.External
                  ? 'external'
                  : 'internal'
              }
              extensionEditable={extensionEditable}
              hasAdvancedCustomization={hasAdvancedCustomization}
              lineOfBusinessCatalog={lineOfBusinessCatalog}
              isProOnlyEntry={isProOnlyEntry}
              hasEntryError={hasRowError}
              onOpenEntry={setOpenEntryIndex}
              onRemoveEntries={removeEntries}
              onAddEntry={addEntry}
              addDisabled={addDisabled}
              addDisabledReason={PRO_UPGRADE_HINT}
            />

            <Slideout
              size="2xl"
              showing={openEntry !== undefined}
              title={
                openEntry
                  ? openEntry.name || openEntry.label || `Entry ${(openEntryIndex ?? 0) + 1}`
                  : ''
              }
              onClose={() => setOpenEntryIndex(null)}
              footer={
                openEntryIndex !== null && (
                  <div className="flex justify-between gap-2">
                    <Button
                      color="red"
                      disabled={isProOnlyEntry(openEntryIndex)}
                      onClick={() => {
                        removeEntry(openEntryIndex);
                        setOpenEntryIndex(null);
                      }}
                    >
                      Remove
                    </Button>
                    <Button color="sky" onClick={() => setOpenEntryIndex(null)}>
                      Done
                    </Button>
                  </div>
                )
              }
            >
              {openEntry && openEntryIndex !== null && (
                <TransferDirectoryEntryFields
                  index={openEntryIndex}
                  entry={openEntry}
                  entryType={entryType}
                  disabled={isProOnlyEntry(openEntryIndex)}
                  extensionEditable={extensionEditable}
                  lineOfBusinessCatalog={lineOfBusinessCatalog}
                  onChange={(patch) => updateEntry(openEntryIndex, patch)}
                  getFieldId={getFieldId}
                  getValidationError={getValidationError}
                />
              )}
            </Slideout>
          </>
        ) : (
          <>
            {ownEntries.map(({ entry, fullIndex }) => {
              const isProOnlyEntry = fullIndex >= 2 && !hasAdvancedCustomization;
              return (
                <div
                  key={fullIndex}
                  className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                      Directory Entry {fullIndex + 1}
                      {isProOnlyEntry && <ProLabel />}
                    </span>
                    <Button
                      color="red"
                      disabled={isProOnlyEntry}
                      onClick={() => removeEntry(fullIndex)}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'transfer',
                        'directory',
                        fullIndex.toString(),
                        'label'
                      ])}
                    >
                      <Label>Name/Department</Label>
                      <Input
                        disabled={isProOnlyEntry}
                        value={entry.label || ''}
                        onChange={(e) =>
                          updateEntry(fullIndex, {
                            label: e.currentTarget.value
                          })
                        }
                        placeholder="e.g., John Smith, Claims Department, Manager"
                        invalid={
                          !!getValidationError([
                            'configuration',
                            'transfer',
                            'directory',
                            fullIndex.toString(),
                            'label'
                          ])
                        }
                      />
                      {getValidationError([
                        'configuration',
                        'transfer',
                        'directory',
                        fullIndex.toString(),
                        'label'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError([
                            'configuration',
                            'transfer',
                            'directory',
                            fullIndex.toString(),
                            'label'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>

                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'transfer',
                        'directory',
                        fullIndex.toString(),
                        'number'
                      ])}
                    >
                      <Label>Phone Number</Label>
                      <Input
                        disabled={isProOnlyEntry}
                        value={entry.number || ''}
                        onChange={(e) => updateEntry(fullIndex, { number: e.currentTarget.value })}
                        onBlur={() => {
                          const formatted = toE164(entry.number || '');
                          if (formatted && formatted !== entry.number) {
                            updateEntry(fullIndex, { number: formatted });
                          }
                        }}
                        placeholder="+1 (555) 123-4567"
                        invalid={
                          !!getValidationError([
                            'configuration',
                            'transfer',
                            'directory',
                            fullIndex.toString(),
                            'number'
                          ])
                        }
                      />
                      {getValidationError([
                        'configuration',
                        'transfer',
                        'directory',
                        fullIndex.toString(),
                        'number'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError([
                            'configuration',
                            'transfer',
                            'directory',
                            fullIndex.toString(),
                            'number'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>

                  <Field
                    data-field-id={getFieldId([
                      'configuration',
                      'transfer',
                      'directory',
                      fullIndex.toString(),
                      'transferCriteria'
                    ])}
                  >
                    <Label>Transfer Criteria (optional)</Label>
                    <Description>
                      Instructions for Cara on when to transfer calls to this person/department. If
                      blank, Cara will transfer the call based on the name or department mentioned.
                    </Description>
                    <Textarea
                      disabled={isProOnlyEntry}
                      value={entry.transferCriteria || ''}
                      onChange={(e) =>
                        updateEntry(fullIndex, { transferCriteria: e.currentTarget.value })
                      }
                      placeholder="Transfer to John for all claims questions and policy inquiries."
                      style={{ minHeight: '80px' }}
                      invalid={
                        !!getValidationError([
                          'configuration',
                          'transfer',
                          'directory',
                          fullIndex.toString(),
                          'transferCriteria'
                        ])
                      }
                    />
                    {getValidationError([
                      'configuration',
                      'transfer',
                      'directory',
                      fullIndex.toString(),
                      'transferCriteria'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError([
                          'configuration',
                          'transfer',
                          'directory',
                          fullIndex.toString(),
                          'transferCriteria'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              );
            })}

            <Button
              disabled={!hasAdvancedCustomization && directory.length >= 2}
              title={
                !hasAdvancedCustomization && directory.length >= 2
                  ? 'Upgrade to Pro to add more than 2 transfer directory entries'
                  : undefined
              }
              color="sky"
              onClick={addEntry}
            >
              Add Directory Entry
              {!hasAdvancedCustomization && directory.length >= 2 && <ProLabel />}
            </Button>
          </>
        )}
      </div>
    </Field>
  );
}
